// Travel Alpine.js component

import { travelService } from './travel.service.js';
import { trackTravelEvent } from '../analytics/fathom.js';
import { queueApiCall, PRIORITY } from '../utils/init-queue.js';
import { shouldLoadProtectedData, hasToken } from '../utils/startup.js';

export const travel = () => ({
  // State
  availableDestinations: [],
  selectedDestination: null,
  preview: null,
  activeTravel: null,
  loading: false,
  error: null,
  currentLocation: 'town',
  isOffline: false,
  
  // Initialization guards
  _initialized: false,
  _syncing: false,
  _loadingDestinations: false,
  _checkingTravel: false,
  
  // Initialize component
  init() {
    if (this._initialized) {
      console.warn('Travel component already initialized');
      return;
    }
    
    console.log('Travel component initialized');
    this._initialized = true;
    
    // Always load current location (cached)
    this.loadCurrentLocation();
    
    // Only load API data if authenticated
    if (hasToken()) {
      this.loadDestinations();
      this.checkActiveTravel();
    } else {
      console.log('⏳ Travel: Waiting for authentication before loading data');
    }
    
    // Check if offline
    this.isOffline = !navigator.onLine;
    window.addEventListener('online', () => {
      this.isOffline = false;
      this.refresh();
    });
    window.addEventListener('offline', () => {
      this.isOffline = true;
    });
    
    // Listen for timer updates
    window.addEventListener('time-update', () => {
      this.checkActiveTravel();
      this.updateTravelDisplay();
    });
    
    // Listen for timer completion
    window.addEventListener('timer-complete', (event) => {
      if (event.detail.timerType === 'travel') {
        this.onTravelComplete();
      }
    });

    // Listen for travel state changes (with debouncing to prevent infinite loops)
    let syncTimeout;
    window.addEventListener('travel:state-changed', (event) => {
      // Skip if this component triggered the event (prevent infinite loops)
      if (event.detail.source === 'component') return;
      
      // Debounce to prevent rapid consecutive calls
      clearTimeout(syncTimeout);
      syncTimeout = setTimeout(() => {
        this.syncWithService();
      }, 100);
    });
    
    // Listen for logout to cleanup  
    window.addEventListener('app:logout', () => this.destroy());
  },
  
  // Load current location
  loadCurrentLocation() {
    this.currentLocation = travelService.getCurrentLocation();
    
    // Update store if needed
    const store = this.$store.app;
    if (store.hasPlayer() && store.p().location !== this.currentLocation) {
      store.updatePlayer({ location: this.currentLocation });
    }
  },
  
  // Load available destinations
  async loadDestinations() {
    if (this._loadingDestinations) return; // Prevent concurrent calls
    
    this._loadingDestinations = true;
    this.loading = true;
    this.error = null;
    
    try {
      // Try cache first if offline
      if (this.isOffline) {
        const cached = await this.getCachedDestinations(this.currentLocation);
        if (cached) {
          this.availableDestinations = cached;
          console.log(`Loaded ${cached.length} destinations from cache for ${this.currentLocation}`);
          return;
        }
      }
      
      // Check if should load protected data
      if (!shouldLoadProtectedData('travel')) {
        console.log('⏳ Travel: Not authenticated or feature disabled');
        return;
      }
      
      // Avoid calling service method that triggers API calls during sync
      if (!this._syncing) {
        // Queue API call to prevent overwhelming the server
        this.availableDestinations = await queueApiCall(
          () => travelService.getAvailableDestinations(),
          PRIORITY.NORMAL
        );
        
        // Cache for offline use
        this.cacheDestinations(this.currentLocation, this.availableDestinations);
        
        console.log(`Loaded ${this.availableDestinations.length} destinations from ${this.currentLocation}`);
      }
    } catch (error) {
      console.error('Failed to load destinations:', error);
      
      // Try cache fallback
      try {
        const cached = await this.getCachedDestinations(this.currentLocation);
        if (cached) {
          this.availableDestinations = cached;
          this.error = 'Usando datos sin conexión';
          return;
        }
      } catch (cacheError) {
        console.warn('Cache fallback failed:', cacheError);
      }
      
      this.error = 'Error al cargar destinos';
    } finally {
      this.loading = false;
      this._loadingDestinations = false;
    }
  },
  
  // Check for active travel
  async checkActiveTravel() {
    if (this._checkingTravel) return; // Prevent concurrent calls
    
    this._checkingTravel = true;
    try {
      // Check if should load protected data
      if (!shouldLoadProtectedData('travel')) {
        console.log('⏳ Travel: Not authenticated or feature disabled');
        return null;
      }
      
      // Only call API method if not during sync to prevent loops
      if (!this._syncing) {
        // Queue API call with higher priority (active travel is important)
        this.activeTravel = await queueApiCall(
          () => travelService.getActive(),
          PRIORITY.HIGH
        );
      } else {
        // During sync, just get cached state without API calls
        this.activeTravel = travelService.state.activeTravel;
      }
      
      if (this.activeTravel) {
        this.$nextTick(() => {
          this.updateTravelDisplay();
        });
      }
    } catch (error) {
      console.error('Failed to check active travel:', error);
    } finally {
      this._checkingTravel = false;
    }
  },
  
  // Sync with service state (prevent recursive calls)
  syncWithService() {
    if (this._syncing) return; // Prevent recursive calls
    
    this._syncing = true;
    try {
      this.loadCurrentLocation();
      this.loadDestinations();
      this.checkActiveTravel();
    } finally {
      this._syncing = false;
    }
  },
  
  // Select destination for preview
  selectDestination(destinationId) {
    if (this.loading || this.activeTravel) return;
    
    this.selectedDestination = destinationId;
    this.error = null;
    
    try {
      this.preview = travelService.preview(destinationId);
      
      // Analytics tracking (only if online)
      if (!this.isOffline) {
        trackTravelEvent('preview', destinationId);
      }
      
      console.log('Travel preview:', this.preview);
      
    } catch (error) {
      console.error('Failed to preview travel:', error);
      this.error = error.message;
      this.preview = null;
    }
  },
  
  // Start travel
  async startTravel() {
    if (!this.selectedDestination || this.loading || this.activeTravel || this.isOffline) return;
    
    this.loading = true;
    this.error = null;
    
    try {
      const travel = travelService.start(this.selectedDestination);
      this.activeTravel = travel;
      
      // Analytics tracking
      trackTravelEvent('start', this.selectedDestination);
      
      // Show success notification
      this.$store.app.addNotification(
        `Viaje iniciado hacia ${travel.toCity.name}`, 
        'success'
      );
      
      // Clear preview
      this.preview = null;
      this.selectedDestination = null;
      
      console.log('Travel started:', travel);
      
    } catch (error) {
      console.error('Failed to start travel:', error);
      this.error = error.message;
      this.$store.app.addNotification(error.message, 'error');
    } finally {
      this.loading = false;
    }
  },
  
  // Cancel travel
  async cancelTravel() {
    if (!this.activeTravel || this.loading || this.isOffline) return;
    
    if (!confirm('¿Estás seguro de que quieres cancelar el viaje?')) {
      return;
    }
    
    this.loading = true;
    
    try {
      travelService.cancel();
      this.activeTravel = null;
      
      // Reload destinations
      this.loadDestinations();
      
      console.log('Travel cancelled');
      
    } catch (error) {
      console.error('Failed to cancel travel:', error);
      this.error = error.message;
    } finally {
      this.loading = false;
    }
  },
  
  // Handle travel completion
  onTravelComplete() {
    this.checkActiveTravel();
    
    if (!this.activeTravel) {
      // Travel completed, reload destinations for new location
      this.loadCurrentLocation();
      this.loadDestinations();
      
      // Analytics tracking
      trackTravelEvent('arrive', this.currentLocation);
      
      this.$store.app.addNotification(
        '¡Has llegado a tu destino!', 
        'success'
      );
    }
  },
  
  // Update travel display
  updateTravelDisplay() {
    if (!this.activeTravel || this.activeTravel.status !== 'traveling') return;
    
    const element = this.$refs.travelTimer;
    if (!element) return;
    
    const timeLeft = this.activeTravel.endsAt - Date.now();
    
    if (timeLeft <= 0) {
      element.textContent = 'Llegando...';
      element.classList.remove('pulse');
    } else {
      const seconds = Math.ceil(timeLeft / 1000);
      const minutes = Math.floor(seconds / 60);
      const secs = seconds % 60;
      
      element.textContent = `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
      
      // Add pulse effect when less than 30 seconds
      if (timeLeft < 30000) {
        element.classList.add('pulse');
      } else {
        element.classList.remove('pulse');
      }
    }
  },
  
  // Check if can travel (not on mission, not already traveling)
  get canTravel() {
    const store = this.$store.app;
    
    if (!store.hasPlayer()) return false;
    
    // Can't travel if already traveling
    if (this.activeTravel && this.activeTravel.status === 'traveling') {
      return false;
    }
    
    // Can't travel if on mission
    if (store.timers?.mission) {
      return false;
    }
    
    // Can't travel if KO'd
    if (store.p().health <= 0) {
      return false;
    }
    
    return true;
  },
  
  // Get block reason message
  get blockReason() {
    const store = this.$store.app;
    
    if (!store.hasPlayer()) return 'Login requerido';
    
    if (this.activeTravel && this.activeTravel.status === 'traveling') {
      return 'Ya estás viajando';
    }
    
    if (store.timers?.mission) {
      return 'No puedes viajar mientras tienes una misión activa';
    }
    
    if (store.p().health <= 0) {
      return 'No puedes viajar estando inconsciente';
    }
    
    return '';
  },
  
  // Check if player can afford travel
  canAffordTravel(cost) {
    if (!this.$store.app.hasPlayer()) return false;
    return this.$store.app.p().money >= cost;
  },
  
  // Format time duration
  formatDuration(minutes) {
    if (minutes < 60) {
      return `${minutes}m`;
    } else {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
    }
  },
  
  // Format arrival time
  formatArrivalTime(endsAt) {
    const date = new Date(endsAt);
    return date.toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  },
  
  // Get current city info
  getCurrentCityInfo() {
    const info = travelService.getCityData(this.currentLocation);
    if (!info) {
      return {
        id: this.currentLocation,
        name: 'Ubicación Desconocida',
        description: 'Descripción no disponible',
        biome: 'plains'
      };
    }
    return info;
  },
  
  // Get destination info
  getDestinationInfo(destinationId) {
    const info = travelService.getCityData(destinationId);
    if (!info) return null;
    return info;
  },
  
  // Format distance
  formatDistance(distance) {
    return `${Math.round(distance)} km`;
  },
  
  // Get biome display info
  getBiomeInfo(biome) {
    const biomes = {
      'plains': { name: 'Llanuras', color: '#10b981', icon: '🌾' },
      'forest': { name: 'Bosque', color: '#059669', icon: '🌲' },
      'mountain': { name: 'Montaña', color: '#64748b', icon: '⛰️' },
      'grassland': { name: 'Pradera', color: '#22c55e', icon: '🌱' },
      'coastal': { name: 'Costa', color: '#06b6d4', icon: '🌊' },
      'desert': { name: 'Desierto', color: '#f59e0b', icon: '🏜️' }
    };
    
    return biomes[biome] || { name: biome, color: '#64748b', icon: '❓' };
  },
  
  // Clear preview
  clearPreview() {
    this.preview = null;
    this.selectedDestination = null;
    this.error = null;
  },
  
  // Refresh component
  refresh() {
    this.loadCurrentLocation();
    this.loadDestinations();
    this.checkActiveTravel();
    this.clearPreview();
  },
  
  // Cache destinations for offline use
  async cacheDestinations(location, destinations) {
    try {
      if ('SimpleReadCache' in window) {
        await window.SimpleReadCache.set(`destinations_${location}`, destinations, { ttl: 300000 }); // 5 min TTL
      }
    } catch (error) {
      console.warn('Failed to cache destinations:', error);
    }
  },
  
  // Get cached destinations
  async getCachedDestinations(location) {
    try {
      if ('SimpleReadCache' in window) {
        return await window.SimpleReadCache.get(`destinations_${location}`);
      }
    } catch (error) {
      console.warn('Failed to get cached destinations:', error);
    }
    return null;
  },
  
  // Cleanup when component is destroyed
  destroy() {
    this._initialized = false;
    this._syncing = false;
    this._loadingDestinations = false;
    this._checkingTravel = false;
    console.log('Travel component destroyed');
  }
});